import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';

// Create React Query client with production optimizations
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Retry strategy for failed requests
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors except 408, 429
        if (error && typeof error === 'object' && 'status' in error) {
          const status = error.status as number;
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
          }
        }
        return failureCount < 3;
      },
      // Cache optimization for financial data - shorter cache time
      staleTime: 2 * 60 * 1000, // 2 minutes (reduced for fresher data)
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      // Disable refetching on mount for cached data
      refetchOnMount: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Performance metrics collection (optional for monitoring)
const reportWebVitals = () => {
  if (import.meta.env.PROD && process.env.VITE_ENABLE_ANALYTICS === 'true') {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      // Send to analytics service
      const vitals = { cls: 0, fid: 0, fcp: 0, lcp: 0, ttfb: 0 };

      getCLS((metric) => { vitals.cls = metric.value; });
      getFID((metric) => { vitals.fid = metric.value; });
      getFCP((metric) => { vitals.fcp = metric.value; });
      getLCP((metric) => { vitals.lcp = metric.value; });
      getTTFB((metric) => { vitals.ttfb = metric.value; });

      // Send to your monitoring service (Sentry, LogRocket, etc.)
      console.log('Web Vitals:', vitals);
    });
  }
};

// Error reporting service setup (essential for production)
const setupErrorReporting = () => {
  if (import.meta.env.PROD && typeof window !== 'undefined') {
    // Global error handler
    window.addEventListener('error', (event) => {
      const errorDetails = {
        type: 'javascript',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        message: event.message,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Send to error reporting service
      if (window.errorReportingService) {
        window.errorReportingService.captureException(event.error, errorDetails);
      }

      // Fallback: Log to console in development
      if (import.meta.env.DEV) {
        console.error('Application Error:', errorDetails);
      }
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const errorDetails = {
        type: 'promise-rejection',
        reason: event.reason,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      if (window.errorReportingService) {
        window.errorReportingService.captureException(event.reason, errorDetails);
      }

      if (import.meta.env.DEV) {
        console.error('Unhandled Promise Rejection:', errorDetails);
      }
    });
  }
};

// Application initialization
const initializeApp = async () => {
  try {
    // Setup error reporting first
    setupErrorReporting();

    // Initialize performance monitoring if enabled
    reportWebVitals();

    // Render the app
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    );

    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </React.StrictMode>
    );

  } catch (error) {
    console.error('Failed to initialize application:', error);

    // Show fallback UI
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.innerHTML = `
        <div style="
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #0a0a0a;
          color: white;
          font-family: system-ui, -apple-system, sans-serif;
          text-align: center;
          padding: 20px;
        ">
          <div>
            <h1 style="font-size: 24px; margin-bottom: 16px;">PayGate</h1>
            <p style="margin-bottom: 20px;">Unable to load application. Please try refreshing.</p>
            <button onclick="window.location.reload()" style="
              background: #3b82f6;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              cursor: pointer;
            ">Refresh Page</button>
          </div>
        </div>
      `;
    }
  }
};

// Start the application
initializeApp();

// Global type declarations for error reporting
declare global {
  interface Window {
    errorReportingService?: {
      captureException: (error: any, context?: any) => void;
      captureMessage: (message: string, context?: any) => void;
      setUser: (user: any) => void;
      clearUser: () => void;
    };
  }
}